import Close from '@mui/icons-material/Close';
import {
	Box,
	Button,
	Checkbox,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	Form,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {
	useEditFieldOptionRegionDetailMutation,
	useGetFieldOptionRegionDetailQuery,
	useCreateFieldOptionRegionDetailMutation,
	useGetFieldOptionCommonDetailQuery,
	useGetFieldOptionCommonIdDetailQuery,
	useEditFieldOptionCommonDetailMutation,
	useCreateFieldOptionCommonDetailMutation,
} from '@/api/field-option/fieldOption';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { FormType } from '@/api/enum/formType.enum';
import { useParams } from 'react-router-dom';
import { PermissionEnum } from '@/api/enum/common.enum';
import { useGetUser } from '@/hooks/useGetUser';

interface Props {
	currentId?: string;
	formType: string;
	type?: number | string;
	parentId?: string;
}

const EditRegionDialog = (props: Props) => {
	const { currentId, formType, type, parentId } = props;
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const [createFieldOptionCommonDetail] =
		useCreateFieldOptionCommonDetailMutation();
	const [editFieldOptionCommonDetail] =
		useEditFieldOptionCommonDetailMutation();
	const { userInfo } = useGetUser();
	useEffect(() => {
		if (!open) methods.reset();
	}, [open]);

	const { data: dictDetails } = useGetFieldOptionCommonDetailQuery(type || '', {
		refetchOnMountOrArgChange: true,
		skip: !type || !open,
	});

	const { data: details } = useGetFieldOptionCommonIdDetailQuery(
		currentId || '',
		{
			refetchOnMountOrArgChange: true,
			skip: !open || !currentId,
		}
	);

	const defaultValues: DefaultValues<any> = {};

	const formSchema = Yup.object().shape({
		code:
			dictDetails?.data?.hascode === true
				? Yup.string().required(t('errorMessage.required'))
				: Yup.string().nullable(),
		labelEn: Yup.string().required(t('errorMessage.required')),
		labelZh:
			dictDetails?.data?.hasChinese === true
				? Yup.string().required(t('errorMessage.required'))
				: Yup.string().nullable(),
	});
	const methods = useForm<any>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});

	useEffect(() => {
		if (details) {
			methods.reset(details.data);
		}
	}, [details, open]);

	const handleClose = () => {
		methods.reset({});
		setOpen(false);
	};

	const onSubmit: SubmitHandler<any> = async (data) => {
		const response =
			formType == FormType.create
				? await createFieldOptionCommonDetail({
						...data,
						type: type,
						parentId: parentId,
				  })
				: await editFieldOptionCommonDetail({
						...data,
						type: type,
						id: currentId,
						parentId: parentId,
				  });
		if ('data' in response) {
			enqueueSnackbar(
				formType == FormType.create
					? t('snackbar.createFieledUnitSuccess')
					: t('snackbar.updateFieledUnitSuccess'),
				{
					variant: 'success',
					anchorOrigin: { horizontal: 'center', vertical: 'top' },
					autoHideDuration: 3000,
					action: (key) => {
						return <CloseIcon onClick={() => closeSnackbar(key)} />;
					},
				}
			);
			handleClose();
		}
	};

	const handleDialog = async () => {
		setOpen(true);
	};

	return (
		<>
			{userInfo?.permissionList?.includes(
				PermissionEnum.OptionManagementCreate
			) && formType == FormType.create ? (
				<Button
					endIcon={<AddCircleOutlineOutlinedIcon />}
					onClick={() => {
						handleDialog();
					}}
					variant='contained'
				>
					{t('field.newOption')}
				</Button>
			) : userInfo?.permissionList?.includes(
					PermissionEnum.OptionManagementEdit
			  ) ? (
				<IconButton
					type='button'
					color='secondary'
					onClick={() => {
						handleDialog();
					}}
				>
					<EditOutlinedIcon />
				</IconButton>
			) : (
				''
			)}

			<Dialog
				open={open}
				onClose={handleClose}
				aria-labelledby='alert-dialog-title'
				aria-describedby='alert-dialog-description'
				maxWidth='xs'
				fullWidth
				onKeyDown={(e) => {
					if (e.key === 'Tab') {
						e.stopPropagation();
					}
				}}
			>
				<form onSubmit={methods.handleSubmit(onSubmit)}>
					<DialogTitle variant='h5'>
						{formType == FormType.create
							? t('field.newOption')
							: t('field.updateOption')}
					</DialogTitle>
					<IconButton
						aria-label='close'
						onClick={handleClose}
						sx={{
							position: 'absolute',
							right: 8,
							top: 8,
							color: (theme) => theme.palette.grey[500],
						}}
					>
						<Close />
					</IconButton>
					<DialogContent>
						<Typography variant='body2' sx={{ marginBottom: '12px' }}>
							{formType == FormType.create
								? t('field.updateOptionTextAdd')
								: t('field.updateOptionText')}
						</Typography>
						<FormProvider {...methods}>
							<Grid
								container
								spacing={2}
								marginTop={0}
								marginBottom={2}
								alignItems='center'
							>
								{dictDetails?.data.hasCode ? (
									<Grid size={12}>
										<AppTextField
											control={methods.control}
											name='code'
											label={t('field.code') + '*'}
										/>
									</Grid>
								) : (
									''
								)}

								<Grid size={12}>
									<AppTextField
										control={methods.control}
										name='labelEn'
										label={t('field.en') + '*'}
									/>
								</Grid>
								{dictDetails?.data.hasChinese ? (
									<Grid size={12}>
										<AppTextField
											control={methods.control}
											name='labelZh'
											label={t('field.zh') + '*'}
										/>
									</Grid>
								) : (
									''
								)}
							</Grid>
						</FormProvider>
					</DialogContent>
					<DialogActions>
						<Button
							sx={{ textTransform: 'none' }}
							onClick={() => handleClose()}
						>
							{t('button.cancel')}
						</Button>
						<Button
							type='submit'
							variant='contained'
							onClick={methods.handleSubmit(onSubmit)}
						>
							{t('button.confirm')}
						</Button>
					</DialogActions>
				</form>
			</Dialog>
		</>
	);
};

export default EditRegionDialog;
